import React, { createContext, useContext, useState, useEffect } from 'react';
import { apiFetch } from '../services/api_service';

const API = process.env.REACT_APP_BACKEND_URL;

const SubscriptionContext = createContext();

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

export const SubscriptionProvider = ({ children }) => {
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Premium features that require Pro/Enterprise subscription
  const PREMIUM_FEATURES = {
    'iro-assessment': { name: 'IRO Assessment Matrix', minPlan: 'pro' },
    'sec-climate': { name: 'SEC Climate Notes', minPlan: 'pro' },
    'gri-sectors': { name: 'GRI Sector Standards', minPlan: 'pro' },
    'sector-comparison': { name: 'Sector Comparison Analytics', minPlan: 'pro' },
    'advanced-analytics': { name: 'Advanced Analytics', minPlan: 'pro' },
    'ai-agents': { name: 'AI Agent Team', minPlan: 'pro' },
    'export-pdf': { name: 'PDF Export', minPlan: 'pro' },
    'export-excel': { name: 'Excel Export', minPlan: 'pro' },
    'fine-tuning': { name: 'Fine-tuning Dashboard', minPlan: 'enterprise' },
    'ab-testing': { name: 'A/B Testing Dashboard', minPlan: 'enterprise' },
  };

  const PLAN_HIERARCHY = {
    'free': 0,
    'pro': 1,
    'enterprise': 2
  };

  const fetchSubscription = async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    if (!token) {
      setSubscription({ plan: 'free', status: 'active' });
      setLoading(false);
      return;
    }

    const data = await apiFetch(`${API}/api/subscription/current`);

    if (data?._error) {
      console.error('Failed to fetch subscription:', data.message);
      // Default to free plan on error
      setSubscription({ plan: 'free', status: 'active' });
      setError(data.message);
    } else {
      setSubscription(data);
      setError(null);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchSubscription();
  }, []);

  // Check if user has access to a specific feature
  const hasAccess = (featureKey) => {
    if (!subscription) return false;
    
    const feature = PREMIUM_FEATURES[featureKey];
    if (!feature) return true; // Unknown features are accessible
    
    const userPlanLevel = PLAN_HIERARCHY[subscription.plan?.toLowerCase()] || 0;
    const requiredPlanLevel = PLAN_HIERARCHY[feature.minPlan] || 0;
    
    return userPlanLevel >= requiredPlanLevel;
  };

  // Check if user is on a specific plan or higher
  const isPlanOrHigher = (planName) => {
    if (!subscription) return false;
    const userPlanLevel = PLAN_HIERARCHY[subscription.plan?.toLowerCase()] || 0;
    const targetPlanLevel = PLAN_HIERARCHY[planName.toLowerCase()] || 0;
    return userPlanLevel >= targetPlanLevel;
  };

  // Get current plan name
  const getCurrentPlan = () => {
    return subscription?.plan || 'free';
  };

  // Check if user is premium (Pro or Enterprise)
  const isPremium = () => {
    return isPlanOrHigher('pro');
  };

  // Check if user is enterprise
  const isEnterprise = () => {
    return isPlanOrHigher('enterprise');
  };

  // Get feature info
  const getFeatureInfo = (featureKey) => {
    return PREMIUM_FEATURES[featureKey] || null;
  };

  const value = {
    subscription,
    loading,
    error,
    hasAccess,
    isPlanOrHigher,
    getCurrentPlan,
    isPremium,
    isEnterprise,
    getFeatureInfo,
    refreshSubscription: fetchSubscription,
    PREMIUM_FEATURES
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export default SubscriptionContext;
